import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UserService, ToastService, ToastType, UserProjectService } from '../../providers';
import { User, ImageFormat, MediaFileType, ImageFormatMap, AllowedMediaMap, Media, MotesPaths, ContainerType, MediaType, ContainerOptions } from '../../models';
import { Animations } from '../../animations';
import { MotesLoginService } from '../../motes-login';
import { ProfileMediaService } from '../../providers/profile-media.service';
import { UploadMediaService } from '../upload-media/upload-media.service';

@Component({
    selector: 'motes-profile',
    templateUrl: './motes-profile.component.html',
    styleUrls: ['./motes-profile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeIn(300)]
})
export class MotesProfileComponent implements OnInit, OnDestroy {

    @Input() editMode: boolean = false;
    @Input() darkMode: boolean = false;

    @Output() loggedOut = new EventEmitter<void>();
    @Output() deleted = new EventEmitter<void>();

    public profilePicFormats: ImageFormatMap = {
        // 'square': new ImageFormat(MediaFileType.SQUARE, true, 1 / 1, 1024),
        'thumb': new ImageFormat(MediaFileType.THUMB, true, 1 / 1, 256, true)
    }

    public coverImageFormats: ImageFormatMap = {
        'hd': new ImageFormat(MediaFileType.HD, true, 16 / 9, 1280, false),
        'thumb': new ImageFormat(MediaFileType.THUMB, true, 1 / 1, 256, true)
    };

    public containerOptions: ContainerOptions;

    public allowedMediaTypes: AllowedMediaMap = { IMAGE: true };
    public user: User = null;
    public profileImage: Media;
    public isUploadPending = false;
    public showProfileOptions: boolean = false;
    public userEdit;
    public showLogoutOverlay: boolean = false;
    public deletingProfile: boolean = false;
    public deleteDescription: string = '';
    public deleteItemName: string = '';
    public deleteLoadingText: string = '';
    public userSubscription;

    // Expose to view
    public MotesPaths = MotesPaths;
    public MediaFileType = MediaFileType;

    constructor(
        private userService: UserService,
        private profileMediaService: ProfileMediaService,
        private loginService: MotesLoginService,
        private uploadMediaService: UploadMediaService,
        private userProjectService: UserProjectService,
        private toastService: ToastService) { }

    ngOnInit() {
        this.profileMediaService.init(this.userService);

        this.userSubscription = this.userService.watchCurrentUser().subscribe((user: User) => {
            this.user = user;
            this.profileImage = (user && !user.isAnonymous) ? user.profilePic : null;
            this.containerOptions = {
                type: ContainerType.PROFILE,
                containerKey: user?.key,
            };
            this.resetUserEdit();
            // Set delete overlay texts
            if(!this.user?.isAdmin && !this.user?.isSuperAdmin) this.deleteItemName = $localize`your profile`;
            else this.deleteDescription = $localize`Deleting your profile also deletes the worlds where you are the owner. We highly recommend transferring ownership of your worlds to other members, before deleting your profile.`;
        })
    }

    public saveProfile(): void {
        this.user.userName = this.userEdit.userName;
        this.user.email = this.userEdit.email;
        // DISABLED with profile marketing toggle: do not persist `isEmailPermitted` from Creator (new consent app).
        // this.user.isEmailPermitted = this.userEdit.isEmailPermitted;
        // this.user.emailPermittedUpdatedOn = this.userEdit.emailPermittedUpdatedOn;
        // Get latest profile image changes before saving profile
        this.user.profilePic = this.profileImage;
        this.userService.updateUser(this.user);
        this.isUploadPending = false;
        this.editMode = false;
    }

    // Kept for when marketing toggle HTML is restored (see .html comment).
    // public togglePermitEmails(permitted: boolean) {
    //     this.userEdit.emailPermittedUpdatedOn = this.userService.getServerTimestamp();
    //     this.userEdit.isEmailPermitted = permitted;
    // }

    public cancel(): void {
        this.resetUserEdit();
        this.editMode = false;
    }

    private resetUserEdit() {
        this.userEdit = { userName: this.user?.userName, email: this.user?.email, isEmailPermitted: this.user?.isEmailPermitted, emailPermittedUpdatedOn: this.user?.emailPermittedUpdatedOn };
    }

    public errorLoadingProfilePic(err): void {
        console.log("Could not load profile pic: ", err);
        this.profileImage.thumb.url = MotesPaths.PROFILE_THUMB_PLACEHOLDER;
        this.user.predefinedProfilePic = '';
    }

    public async setPredefinedProfilePic() {
        // Delete profile pic thumb if it is not predefined
        if(!this.user.predefinedProfilePic) await this.uploadMediaService.deleteFiles(this.profileImage?.thumb, this.profileImage, true);
        // Set predefined profile pic
        let predefinedMedia = await this.profileMediaService.getPredefinedMedia(this.user.predefinedProfilePic);
        this.profileImage.thumb.url = predefinedMedia.thumb.url;
        this.profileImage.type = MediaType.IMAGE;
        // Update profile image on user
        this.user.profilePic = this.profileImage;
        this.user.predefinedProfilePic = predefinedMedia.key;
        this.userService.updateUser(this.user);
    }

    public profileUploaded(media: Media) {
        this.isUploadPending = false;
        this.user.profilePic = media;
        this.user.predefinedProfilePic = '';
        // Update predefined profile pic (don't save entire user as this.user might be in sync at this point)
        this.userService.updatePredefinedProfilePic(this.user.key, '');
    }

    public uploadPending(media: Media) {
        this.isUploadPending = true;
    }

    public logOutClicked() {
        this.showLogoutOverlay = true;
    }

    public async logOut() {
        await this.loginService.logout();
        this.loggedOut.emit();
    }

    public onProfileImageDeleted() {
        this.setPredefinedProfilePic();
    }

    public async deleteProfile() {
        this.deletingProfile = true;
        // Stop watching current user
        this.ngOnDestroy();
        // Delete db user, firebase user, profile pic media files, and owned worlds
        try {
            this.deleteLoadingText = $localize`Deleting your worlds. Please wait. `;
            await this.userProjectService.deleteUserProjects(this.user.key);
            // Delete custom profile pic
            if(!this.user.predefinedProfilePic) {
                this.deleteLoadingText = $localize`Deleting your profile picture. Please wait. `;
                await this.uploadMediaService.deleteFiles(this.profileImage?.thumb, this.profileImage, true);
            }
            this.deleteLoadingText = $localize`Deleting your profile. Please wait. `;
            await this.userService.deleteUser(this.user.key);
            await this.userService.deleteFirebaseUser(this.user.key);
        } catch(err) {
            this.toastService.addToast($localize`Oh no, for some reason we couldn't delete your profile. Please try again, or contact us.`, ToastType.ERR, 10000);
            console.log(err.message);
        }
        this.deletingProfile = false;
        this.deleteLoadingText = '';
        // Log out to update view
        await this.logOut();
        this.toastService.addToast($localize`Your profile is deleted.`, ToastType.OK)
        this.deleted.emit();
    }

    ngOnDestroy(): void {
        if(this.userSubscription) this.userSubscription.unsubscribe();
    }
}
